import { AxiosError, Email } from '@price-for-profit/micro-services';
import md5 from 'blueimp-md5';
import {
  addMonths,
  endOfMonth,
  endOfWeekWithOptions,
  format,
  startOfMonth,
  startOfWeekWithOptions,
  subMonths,
  subWeeks,
  compareAsc,
} from 'date-fns/fp';
import { formatISO } from 'date-fns';
import { format as timeZoneFormat, utcToZonedTime } from 'date-fns-tz';
import {
  APPROVAL_EMAILS,
  CLIENT_ID,
  CONTRACT_EMAIL_TEMPLATES,
  CONTROLLER_BRAND_EMAILS,
  CUSTOMER_DELETE_APPROVAL_EMAILS,
  CUSTOMER_EMAIL_TEMPLATES,
  START_OF_WEEK,
} from 'shared/constants';
import {
  ContractDocument,
  ContractNotificationTemplate,
  I2PEnvironment,
  CustomerContractSummaryGrossNetPercentGridItem,
  CustomerNotificationTemplate,
  CustomerLookup,
} from 'shared/types';
import { unparse } from 'papaparse';

const moneyFormatter = new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
const percentFormatter = new Intl.NumberFormat('en', {
  style: 'percent',
  minimumFractionDigits: 2,
  minimumIntegerDigits: 2,
});
export const formatMoney = (value: number) => moneyFormatter.format(value);
export const formatPercent = (value: number) => percentFormatter.format(value);

const EST = 'America/New_York';

export const formatDate = format('MM-dd-yy');
export const formatISODate = (date: Date) => formatISO(date, { representation: 'date' });
export const formatDateSlashed = format('M/dd/yyyy');
export const formatFullMonthDateSlashed = format('MM/dd/yyyy');
export const formatDateTime = format("M/dd/yyyy h:mmaaaaa'm'");
export const formatYearMonthDayDate = format('yyyyMMdd');
export const formatDateTimeEST = (date: string | number | Date) =>
  timeZoneFormat(utcToZonedTime(date, EST), "M/dd/yyyy h:mmaaaaa'm'", { timeZone: EST });
export const compareDates = (firstDate: Date, secondDate: Date) => compareAsc(firstDate, secondDate);

const addOneMonth = addMonths(1);
const minusOneMonth = subMonths(1);
const minusOneWeek = subWeeks(1);
const startOfWeek = startOfWeekWithOptions({ weekStartsOn: START_OF_WEEK });
const endOfWeek = endOfWeekWithOptions({ weekStartsOn: START_OF_WEEK });

export const getExportStartEnd = (today: Date) => [
  formatYearMonthDayDate(minusOneMonth(startOfMonth(today))),
  formatYearMonthDayDate(addOneMonth(endOfMonth(today))),
];

export const getWeekExportStartEnd = (today: Date) => [startOfWeek(minusOneWeek(today)), endOfWeek(today)];

export const round = (value: number, places: number = 2) => (isNaN(value) ? 0 : Number(value.toFixed(places)));

export function browserDownloadCsv(csv: string, fileName: string) {
  const blobUrl = URL.createObjectURL(new Blob([csv]));
  const link = document.createElement('a');

  link.href = blobUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  link.parentNode?.removeChild(link);
}

export const withExtension = (extension: string) => (value: string) => value.concat(extension);
export const addPdfExtension = withExtension('.pdf');

export const hashFileName = (phrase: string) => md5(phrase);

export const replaceDashBySlash = (text: string) => text.replace(/-/g, '/');

export const toFixed = (places: number) => (value: number) => Number(Number(value).toFixed(places));

export const fixed2Places = toFixed(2);

export const fixed3Places = toFixed(3);

export const getNestedFieldName = (prefix: string) => (index: number) => (field: string) => {
  const combined = `${prefix}.${index}`;
  return `${combined}.${field}` as const;
};

export function isAxiosError<T>(error: any): error is AxiosError<T> {
  return error.isAxiosError;
}

export function buildEmail({
  subject,
  body,
  to,
  cc,
}: {
  subject: string;
  body: string;
  to: string[];
  cc: string[];
}): Email {
  return {
    actor: 'ABG Retail Drive Application',
    clientId: CLIENT_ID,
    subject,
    body,
    to,
    cc,
    bcc: [],
  };
}

export const buildContractUrl = (contract: ContractDocument) => {
  const appUrl = `${window.location.protocol}//${window.location.host}`;
  return `${appUrl}/customer-management/customer-details/${encodeURIComponent(
    contract.customerName
  )}/${encodeURIComponent(contract.brand)}/viewContract/${contract.id}`;
};

export const buildCustomerUrl = (customer: CustomerLookup) => {
  const appUrl = `${window.location.protocol}//${window.location.host}`;
  return `${appUrl}/customer-management/customer-details/${encodeURIComponent(
    customer.abgmastercustomer
  )}/${encodeURIComponent(customer.brand)}/contractSummary`;
};

export interface supportEmailTemplateProps {
  comment: string;
  user: string;
  userEmail: string;
  emailSentTo: string;
}

export function getSupportEmailTemplate({ comment, user, userEmail, emailSentTo }: supportEmailTemplateProps) {
  return buildEmail({
    subject: 'Quote Application Feedback',
    body: `
    The following feedback was submitted for the ABG Quoting App:

    Name: ${user}
    Email: ${userEmail}
    Comment: ${comment}
    `,
    to: [emailSentTo],
    cc: [],
  });
}

interface GetContractEmailTemplateProps {
  contract: ContractDocument;
  template: ContractNotificationTemplate;
  environment: I2PEnvironment;
  comments: string;
}
export function getContractEmailTemplate({
  contract,
  template,
  environment,
  comments,
}: GetContractEmailTemplateProps): Email {
  const modifiedUser = [contract.modifiedByEmail];
  const approvedUser = [contract.approvedByEmail] ?? [contract.approvedBy];
  let to =
    template === 'request' || template === 'cancel'
      ? APPROVAL_EMAILS[environment]
      : template === 'delete'
      ? approvedUser
      : modifiedUser;
  const cc =
    template === 'request' || template === 'cancel' || template === 'delete'
      ? modifiedUser
      : APPROVAL_EMAILS[environment];

  if (template === 'approve') {
    try {
      to = to.concat(CONTROLLER_BRAND_EMAILS[environment][contract.brand.toUpperCase()]);
      if (environment === 'prod') {
        to.concat('nsaboo@insight2profit.com');
      }
    } catch {
      console.log('No controller found for brand: ', contract.brand.toUpperCase());
    }
  }

  const { subject, body } = CONTRACT_EMAIL_TEMPLATES[template]({ contract, comments });

  return buildEmail({
    subject,
    body,
    to,
    cc,
  });
}
export const exportContractGrossNet = (
  name: string,
  grossData: CustomerContractSummaryGrossNetPercentGridItem[],
  formatFunc: (value: number) => string
) => {
  const fileName = `${name}.csv`;
  const fields = ['Contract', 'Brand', 'LTM Revenue', 'Allowances', 'Co-op', 'Payment Terms', 'Rebates', 'Other'];
  const dataToExport = grossData.map(dataPeice => {
    return {
      originalDocumentName: dataPeice.originalDocumentName,
      brand: dataPeice.brand,
      ltmRevenue: formatFunc(dataPeice.ltmRevenue),
      allowances: formatFunc(dataPeice.allowances),
      coop: formatFunc(dataPeice.coop),
      paymentTerms: formatFunc(dataPeice.paymentTerms),
      rebates: formatFunc(dataPeice.rebates),
      other: formatFunc(dataPeice.other),
    };
  });
  const csvData = dataToExport.map(record => Object.values(record));
  const csv = unparse({ data: csvData, fields });
  browserDownloadCsv(csv, fileName);
};

interface GetCustomerDeleteEmailTemplateProps {
  customer: CustomerLookup;
  template: CustomerNotificationTemplate;
  environment: I2PEnvironment;
  userName: string;
}
export function getCustomerDeleteEmailTemplate({
  customer,
  template,
  environment,
  userName,
}: GetCustomerDeleteEmailTemplateProps): Email {
  let to = CUSTOMER_DELETE_APPROVAL_EMAILS[environment];
  const cc = CUSTOMER_DELETE_APPROVAL_EMAILS[environment];

  const { subject, body } = CUSTOMER_EMAIL_TEMPLATES[template]({ customer, userName });

  return buildEmail({
    subject,
    body,
    to,
    cc,
  });
}

export function handleBlob(blob: Blob, fileName: string) {
  const blobUrl = URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  link.parentNode?.removeChild(link);
}
